import React from 'react'
import Link from 'next/link'
import type {
  PaginationLinkComponentProps,
  PrevNextPageLinkProps,
} from './PaginationLinks'

export interface CreatePaginationLinkComponentProps {
  basePath: string
}

export function createPaginationLinkComponent({
  basePath,
}: CreatePaginationLinkComponentProps) {
  return function PaginationLinkComponent({
    pageInfo: { pageNumber, isCurrentPage },
  }: PaginationLinkComponentProps) {
    let href = basePath
    const pageNumberString = String(pageNumber)
    if (pageNumber > 1) {
      href = `${basePath}/page-${pageNumberString}`
    }
    const ariaLabel = isCurrentPage
      ? `Current Page, Page ${pageNumberString}`
      : `Go to Page ${pageNumberString}`
    const baseStyle = 'p-2 rounded px-3 text-center'
    const inactiveStyle = `${baseStyle} hover:bg-blue-100`
    const activeStyle = `${baseStyle} bg-blue-500 text-white`
    return (
      <li>
        <Link
          href={href}
          className={isCurrentPage ? activeStyle : inactiveStyle}
          aria-label={ariaLabel}
          aria-current={isCurrentPage ? 'page' : false}
        >
          {pageNumber}
        </Link>
      </li>
    )
  }
}

export function PaginationEllipsis() {
  return (
    <li>
      <span className="rounded p-2 px-3 text-center">...</span>
    </li>
  )
}

export interface GeneratePrevNextPageLinkComponentProps {
  linkText: string
  basePath: string
}

export function createPrevNextPageLinkComponent({
  linkText,
  basePath,
}: GeneratePrevNextPageLinkComponentProps) {
  return function PrevPageLink({
    pageNumber,
    isDisabled,
  }: PrevNextPageLinkProps) {
    let href = basePath
    const pageNumberString = String(pageNumber)
    if (pageNumber > 1) {
      href = `${basePath}/page-${pageNumberString}`
    }
    const ariaLabel = `Go to previous page, page ${pageNumberString}`
    const style = 'p-2 rounded px-3 text-center hover:bg-blue-100'
    const disabledStyle = 'text-slate-400 pointer-events-none'
    return (
      <li className={isDisabled ? disabledStyle : ''}>
        <Link href={href}>
          <span className={style} aria-label={ariaLabel}>
            {linkText}
          </span>
        </Link>
      </li>
    )
  }
}
